import { useEffect, useState, useMemo } from 'react';
import { Loader2, Info, Plus, Pencil, Trash2, Search, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Switch } from '@/core/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/core/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/core/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/core/components/ui/popover';
import { useOperatorStore } from '@/core/stores/operator-store';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { cn } from '@/core/lib/utils';
import { servicesContainer } from '@/core/di/services-container';
import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { BillingApi } from '../../data/billing-api';
import { useBillingStore } from '../../stores/billing-store';
import type { OperatorBillingConfig, UpsertOperatorBillingConfigRequest } from '../../types/billing-types';

interface IdNameItem { id: number; name: string }

function SearchableIdSelect({ items, value, onChange, placeholder = 'Select…', disabled = false, emptyText = 'No items available' }: {
  items: IdNameItem[]; value: string; onChange: (v: string) => void;
  placeholder?: string; disabled?: boolean; emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return [...items].sort((a, b) => a.name.localeCompare(b.name));
    return items
      .map(item => {
        const n = item.name.toLowerCase(), id = String(item.id);
        const score = n === q ? 100 : n.startsWith(q) ? 90 : n.includes(q) ? 70
          : id === q ? 85 : id.startsWith(q) ? 60 : id.includes(q) ? 40 : 0;
        return { item, score };
      })
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
      .map(r => r.item);
  }, [items, search]);

  const selected = items.find(i => String(i.id) === value);

  return (
    <Popover modal={false} open={open && !disabled} onOpenChange={o => { if (!disabled) { setOpen(o); if (!o) setSearch(''); } }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" disabled={disabled} className="w-full justify-between font-normal"
          onClick={() => !disabled && setOpen(o => !o)}>
          <span className={selected ? 'text-gray-900' : 'text-gray-400'}>{selected ? selected.name : placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" value={search} onValueChange={setSearch} />
          <CommandList className="max-h-60 overflow-y-auto" onWheel={e => e.stopPropagation()}>
            <CommandEmpty><p className="text-sm text-gray-500 text-center py-4">{emptyText}</p></CommandEmpty>
            <CommandGroup>
              {filtered.map(item => (
                <CommandItem key={item.id} value={`${item.name} ${item.id}`}
                  onSelect={() => { onChange(String(item.id)); setOpen(false); setSearch(''); }}
                  className="flex items-center gap-2 cursor-pointer">
                  <Check className={cn('mr-2 h-4 w-4', value === String(item.id) ? 'opacity-100' : 'opacity-0')} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{item.name}</span>
                    <span className="text-xs text-gray-500">ID: {item.id}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const emptyForm = (operatorId = 0): UpsertOperatorBillingConfigRequest => ({
  operatorId,
  ocpiConnectorsThreshold: 0,
  noMsfForDc: false,
  isEnergyCsms: false,
  isApp: false,
});

export function OperatorConfigTab() {
  const { selectedOperator, selectOperator, operators, loadOperators, isLoading: operatorsLoading } = useOperatorStore();
  const {
    operatorConfigs,
    isLoadingOperatorConfigs,
    operatorConfigExists,
    isSavingOperatorConfig,
    operatorConfigError,
    loadOperatorConfigs,
    loadOperatorConfig,
    saveOperatorConfig,
    deleteOperatorConfig,
  } = useBillingStore();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<OperatorBillingConfig | null>(null);
  const [formData, setFormData] = useState<UpsertOperatorBillingConfigRequest>(emptyForm());
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [distributors, setDistributors] = useState<IdNameItem[]>([]);
  const [formDistributorId, setFormDistributorId] = useState<string>('');

  useEffect(() => {
    const services = servicesContainer.getServices();
    const api = new BillingApi(BASE_AUTH_OFFICE_API, services.tokenManager);
    api.getDistributors().then(list => setDistributors(list.map(d => ({ id: d.id, name: d.name }))));
  }, []);

  const sheetOperators = useMemo((): IdNameItem[] => {
    const filtered = formDistributorId
      ? operators.filter(o => o.distributorId === Number(formDistributorId))
      : operators;
    return filtered.map(o => ({ id: o.operatorId, name: o.name }));
  }, [operators, formDistributorId]);

  const [operatorFilter, setOperatorFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pageSizeOptions = [10, 25, 50, 100];

  const filteredConfigs = useMemo(() => {
    const term = operatorFilter.trim().toLowerCase();
    if (!term) return operatorConfigs;
    return operatorConfigs.filter(c =>
      c.operatorId.toString().includes(term) ||
      c.operatorName?.toLowerCase().includes(term) ||
      c.distributorId?.toString().includes(term)
    );
  }, [operatorConfigs, operatorFilter]);

  useEffect(() => { setCurrentPage(1); }, [operatorFilter]);

  const totalCount = filteredConfigs.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);
  const pagedConfigs = filteredConfigs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) startPage = Math.max(1, endPage - maxPagesToShow + 1);
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  useEffect(() => {
    loadOperatorConfigs();
    if (operators.length === 0 && !operatorsLoading) loadOperators();
  }, [loadOperatorConfigs, loadOperators]);

  const openAdd = () => {
    setEditingConfig(null);
    setFormDistributorId('');
    setFormData(emptyForm());
    setSheetOpen(true);
  };

  const openEdit = (config: OperatorBillingConfig) => {
    setEditingConfig(config);
    setFormDistributorId(config.distributorId != null ? String(config.distributorId) : '');
    setFormData({
      operatorId: config.operatorId,
      ocpiConnectorsThreshold: config.ocpiConnectorsThreshold,
      noMsfForDc: config.noMsfForDc,
      isEnergyCsms: config.isEnergyCsms,
      isApp: config.isApp,
    });
    loadOperatorConfig(config.operatorId);
    setSheetOpen(true);
  };

  const handleOperatorChange = (operatorIdStr: string) => {
    const operator = operators.find(op => op.operatorId.toString() === operatorIdStr);
    if (operator) {
      selectOperator(operator);
      setFormData(f => ({ ...f, operatorId: operator.operatorId }));
      loadOperatorConfig(operator.operatorId);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveOperatorConfig(formData);
      await loadOperatorConfigs();
      toast.success(editingConfig ? 'Config updated' : 'Config created');
      setSheetOpen(false);
    } catch {
      toast.error(operatorConfigError ?? 'Failed to save config');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (operatorId: number) => {
    setDeletingId(operatorId);
    try {
      await deleteOperatorConfig(operatorId);
      toast.success('Config deleted');
    } catch {
      toast.error('Failed to delete config');
    } finally {
      setDeletingId(null);
    }
  };

  const formatBool = (val: boolean | null | undefined) =>
    val ? <span className="text-green-600 font-medium">Yes</span> : <span className="text-gray-400">No</span>;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Filter by operator name or ID..."
            value={operatorFilter}
            onChange={e => setOperatorFilter(e.target.value)}
            className="pl-8 pr-8 h-9"
          />
          {operatorFilter && (
            <button
              onClick={() => setOperatorFilter('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Button
          onClick={openAdd}
          className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold h-9"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add Config
        </Button>
      </div>

      {/* Table */}
      <div className={`rounded-md border ${GRID_STYLES.border} overflow-hidden`}>
        {isLoadingOperatorConfigs ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <table className="w-full">
            <thead className={cn(GRID_STYLES.header.background, 'sticky top-0 z-10')}>
              <tr>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Distributor</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Operator</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'text-center')}>OCPI Threshold</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'text-center')}>No MSF for DC</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'text-center')}>Is Energy CSMS</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'text-center')}>Is App</th>
                <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)} />
              </tr>
            </thead>
            <tbody>
              {pagedConfigs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-gray-400 py-12">
                    {operatorFilter ? 'No operators match your filter' : 'No operator configs found'}
                  </td>
                </tr>
              ) : (
                pagedConfigs.map((config) => (
                  <tr key={config.operatorId} className={cn(GRID_STYLES.row.default, GRID_STYLES.row.hover, `border-b ${GRID_STYLES.border}`, 'transition-colors')}>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-700')}>
                      {config.distributorId ?? ''}
                    </td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-gray-700')}>
                      {config.operatorId}{config.operatorName ? <span className="text-gray-400"> ({config.operatorName})</span> : null}
                    </td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-center text-gray-700')}>{config.ocpiConnectorsThreshold ?? ''}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-center')}>{formatBool(config.noMsfForDc)}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-center')}>{formatBool(config.isEnergyCsms)}</td>
                    <td className={cn(GRID_STYLES.cell.base, 'text-center')}>{formatBool(config.isApp)}</td>
                    <td className={GRID_STYLES.cell.base}>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(config)} className="text-gray-400 hover:text-gray-700 p-1">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(config.operatorId)}
                          disabled={deletingId === config.operatorId}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          {deletingId === config.operatorId
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Trash2 className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between px-2 py-2 border border-[#E8E8E8] rounded-b-md bg-white -mt-3 border-t-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map(pageNum => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentPage(pageNum)}
                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] border-[#FFCD00]' : 'border-[#E8E8E8]'}`}
              >
                {pageNum}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#606060] ml-2">{currentPage} of {totalPages} pages</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalCount.toLocaleString()}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#606060]">Rows per page:</span>
              <Select value={pageSize.toString()} onValueChange={v => { setPageSize(parseInt(v, 10)); setCurrentPage(1); }}>
                <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map(size => (
                    <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editingConfig ? 'Edit Config' : 'Add Config'}</SheetTitle>
          </SheetHeader>

          <div className="space-y-5 mt-6">
            {/* Distributor */}
            <div className="space-y-1.5">
              <Label>Distributor <span className="text-red-500">*</span></Label>
              {editingConfig ? (
                <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm">
                  <span className="font-medium text-gray-900">
                    {distributors.find(d => d.id === editingConfig.distributorId)?.name ?? editingConfig.distributorId ?? ''}
                  </span>
                  {editingConfig.distributorId && <span className="ml-2 text-xs text-gray-400 font-mono">{editingConfig.distributorId}</span>}
                </div>
              ) : (
                <SearchableIdSelect
                  items={distributors}
                  value={formDistributorId}
                  onChange={v => { setFormDistributorId(v); setFormData(f => ({ ...f, operatorId: 0 })); }}
                  placeholder="Select distributor…"
                  emptyText="No distributors found"
                />
              )}
            </div>

            {/* Operator */}
            <div className="space-y-1.5">
              <Label>Operator <span className="text-red-500">*</span></Label>
              {editingConfig ? (
                <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm">
                  <span className="font-medium text-gray-900">
                    {operators.find(o => o.operatorId === editingConfig.operatorId)?.name ?? editingConfig.operatorId}
                  </span>
                  <span className="ml-2 text-xs text-gray-400 font-mono">{formData.operatorId}</span>
                </div>
              ) : (
                <SearchableIdSelect
                  items={sheetOperators}
                  value={formData.operatorId ? String(formData.operatorId) : ''}
                  onChange={v => {
                    const op = operators.find(o => o.operatorId === Number(v));
                    if (op) { selectOperator(op); }
                    setFormData(f => ({ ...f, operatorId: Number(v) }));
                    if (Number(v)) loadOperatorConfig(Number(v));
                  }}
                  placeholder="Select operator…"
                  disabled={!formDistributorId}
                  emptyText={formDistributorId ? 'No operators for this distributor' : 'Select a distributor first'}
                />
              )}
            </div>

            {/* OCPI Connectors Threshold */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="ocpiConnectorsThreshold">OCPI Connectors Threshold</Label>
                <InfoTooltip text="Minimum number of OCPI connectors required before billing applies" />
              </div>
              <Input
                id="ocpiConnectorsThreshold"
                type="number"
                min={0}
                value={formData.ocpiConnectorsThreshold ?? ''}
                onChange={(e) =>
                  setFormData({ ...formData, ocpiConnectorsThreshold: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })
                }
                placeholder="None"
                className="w-24 text-center"
              />
            </div>

            {/* Toggles */}
            <div className="space-y-4 pt-1">
              {([
                { id: 'noMsfForDc', label: 'No MSF for DC', tooltip: 'Disable Monthly Service Fee for DC charging sessions' },
                { id: 'isEnergyCsms', label: 'Is Energy CSMS', tooltip: 'Operator uses an energy-based Charging Management System' },
                { id: 'isApp', label: 'Is App', tooltip: 'Operator has a mobile app integration' },
              ] as const).map(({ id, label, tooltip }) => (
                <div key={id} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor={id} className="cursor-pointer font-normal">{label}</Label>
                    <InfoTooltip text={tooltip} />
                  </div>
                  <Switch
                    id={id}
                    checked={!!formData[id]}
                    onCheckedChange={(checked) => setFormData({ ...formData, [id]: checked })}
                  />
                </div>
              ))}
            </div>

            {operatorConfigError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {operatorConfigError}
              </div>
            )}

            <div className="pt-2">
              <Button
                onClick={handleSave}
                disabled={isSaving || !formData.operatorId || (!editingConfig && !formDistributorId)}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold"
              >
                {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
